/**
 * MyPayrollReport — employee-facing comprehensive monthly report.
 * Shows payslip + attendance + leave + YTD, gated on HR verify+approve.
 * Print-friendly (CSS @media print) — user can use browser "Save as PDF".
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Loader2, Printer, FileText, Calendar as CalendarIcon, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_BASE as API } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('hrms-token') || ''}`,
});

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function MyPayrollReport() {
  const { user } = useAuth();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [employeeId, setEmployeeId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resolve employee_id for the current user
  useEffect(() => {
    (async () => {
      if (!user) return;
      // If user is an admin/hr they can pass an employee_id manually; otherwise resolve own.
      try {
        const r = await fetch(`${API}/api/employees/me`, { headers: authHeaders(), credentials: 'include' });
        const d = await safeJson(r);
        if (r.ok && d?.employee_id) setEmployeeId(d.employee_id);
      } catch (_) { /* ignore */ }
    })();
  }, [user]);

  const fetchReport = async (overrideEmp) => {
    const empId = overrideEmp || employeeId;
    if (!empId) { toast.error('Please enter an Employee ID'); return; }
    if (!month) { toast.error('Please select a month'); return; }
    setLoading(true); setError(''); setReport(null);
    try {
      const r = await fetch(`${API}/api/payroll/employee/${empId}/comprehensive-report?month=${month}`, {
        headers: authHeaders(), credentials: 'include',
      });
      const d = await safeJson(r);
      if (!r.ok) {
        setError(d.detail || 'Could not load report'); return;
      }
      setReport(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const print = () => window.print();

  return (
    <div className="space-y-6 print:p-0" data-testid="my-payroll-report">
      {/* Header — hidden on print */}
      <div className="flex items-start justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" /> My Comprehensive Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Payslip + Attendance + Leave summary. Visible only after HR verifies & approves the payroll.
          </p>
        </div>
        {report && (
          <Button onClick={print} variant="outline" data-testid="print-report-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
          </Button>
        )}
      </div>

      {/* Selector */}
      <Card className="print:hidden" data-testid="report-selector-card">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label htmlFor="emp-id">Employee ID</Label>
              <Input id="emp-id" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="Auto-filled for own report" data-testid="emp-id-input" />
            </div>
            <div>
              <Label htmlFor="rep-month">Month</Label>
              <Input id="rep-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input" />
            </div>
            <Button onClick={() => fetchReport()} disabled={loading} data-testid="load-report-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-1.5" />}
              Load Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-amber-300 bg-amber-50 print:hidden" data-testid="report-error-card">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-amber-900">Report not available</div>
              <div className="text-sm text-amber-800 mt-1">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report */}
      {report && (
        <div className="space-y-4 print:space-y-2" data-testid="report-content">
          {/* Employee header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="text-2xl font-bold">{report.employee.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {report.employee.designation} · {report.employee.department}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">{report.employee.employee_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Pay Month</div>
                  <div className="text-xl font-bold">{report.month}</div>
                  <Badge className="bg-emerald-100 text-emerald-800 mt-1">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Verified & Approved
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Summary */}
          <Card data-testid="attendance-summary-card">
            <CardHeader><CardTitle>Attendance Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <Stat label="Working Days" value={report.attendance.working_days} />
                <Stat label="Present" value={report.attendance.present_days} accent="emerald" />
                <Stat label="Approved Leaves" value={report.payslip.approved_leave_days} accent="blue" />
                <Stat label="Unapproved Absent" value={report.payslip.unapproved_absent_days} accent="red" />
              </div>
              <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-center">
                <div className="text-xs uppercase text-indigo-700">Payable Days</div>
                <div className="text-3xl font-bold text-indigo-900 mt-1">{report.payslip.payable_days}</div>
                <div className="text-xs text-indigo-700 mt-1">= Present ({report.payslip.present_days}) + Approved Leave ({report.payslip.approved_leave_days})</div>
              </div>
            </CardContent>
          </Card>

          {/* Leave Summary */}
          <Card data-testid="leave-summary-card">
            <CardHeader><CardTitle>Leave Summary</CardTitle></CardHeader>
            <CardContent>
              {Object.keys(report.leave_taken_in_month).length > 0 ? (
                <div className="space-y-1 mb-3">
                  <div className="font-semibold text-sm">Taken this month:</div>
                  <ul className="list-disc list-inside text-sm">
                    {Object.entries(report.leave_taken_in_month).map(([t, d]) => (
                      <li key={t} data-testid={`leave-taken-${t}`}>{t}: <span className="font-mono">{d} day{d > 1 ? 's' : ''}</span></li>
                    ))}
                  </ul>
                </div>
              ) : (<p className="text-sm text-muted-foreground mb-3">No leaves taken in {report.month}</p>)}

              {report.leave_balances.length > 0 && (
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr><th className="p-2 text-left">Leave Type</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Used</th><th className="p-2 text-right">Balance</th></tr>
                  </thead>
                  <tbody>
                    {report.leave_balances.map((b) => (
                      <tr key={b.leave_type} className="border-t" data-testid={`balance-row-${b.leave_type}`}>
                        <td className="p-2">{b.leave_type}</td>
                        <td className="p-2 text-right font-mono">{b.total}</td>
                        <td className="p-2 text-right font-mono">{b.used}</td>
                        <td className="p-2 text-right font-mono font-semibold">{b.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Payslip */}
          <Card data-testid="payslip-card">
            <CardHeader><CardTitle>Payslip — {report.month}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold text-sm mb-2">Earnings</div>
                  <Row label="Basic" value={fmt(report.payslip.basic_salary)} />
                  <Row label="DA" value={fmt(report.payslip.da)} />
                  <Row label="HRA" value={fmt(report.payslip.hra)} />
                  <Row label="TA" value={fmt(report.payslip.ta)} />
                  {report.payslip.consolidated_salary > 0 && (
                    <Row label="Consolidated" value={fmt(report.payslip.consolidated_salary)} />
                  )}
                  <Row label="Gross" value={fmt(report.payslip.gross_salary)} bold />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-2">Deductions</div>
                  <Row label="PF" value={`-${fmt(report.payslip.pf)}`} />
                  <Row label="ESI" value={`-${fmt(report.payslip.esi)}`} />
                  <Row label="Prof. Tax" value={`-${fmt(report.payslip.professional_tax)}`} />
                  <Row label="TDS" value={`-${fmt(report.payslip.tds)}`} />
                  <Row label="Late Penalty" value={`-${fmt(report.payslip.late_penalty)}`} />
                  <Row label="LOP (unapproved absences)" value={`-${fmt(report.payslip.lop_amount)}`} />
                  <Row label="Total Deductions" value={`-${fmt(report.payslip.total_deductions)}`} bold />
                </div>
              </div>
              <div className="mt-4 p-4 bg-emerald-50 rounded-lg text-center" data-testid="net-pay">
                <div className="text-sm text-emerald-700 uppercase">Net Salary</div>
                <div className="text-3xl font-bold text-emerald-900 mt-1">{fmt(report.payslip.net_salary)}</div>
              </div>
            </CardContent>
          </Card>

          {/* YTD */}
          <Card data-testid="ytd-card">
            <CardHeader><CardTitle>Year to Date (Financial Year)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                <Stat label="Months Paid" value={report.ytd.months_paid} />
                <Stat label="YTD Gross" value={fmt(report.ytd.gross)} />
                <Stat label="YTD Net" value={fmt(report.ytd.net)} accent="emerald" />
                <Stat label="YTD PF" value={fmt(report.ytd.pf)} />
                <Stat label="YTD TDS" value={fmt(report.ytd.tds)} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === 'emerald' ? 'text-emerald-600' :
              accent === 'blue' ? 'text-blue-600' :
              accent === 'red' ? 'text-red-600' : 'text-slate-900';
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between py-1 text-sm border-b last:border-b-0 ${bold ? 'font-semibold' : ''}`}>
      <span>{label}</span><span className="font-mono">{value}</span>
    </div>
  );
}
